import React from 'react';
import { AppContext } from '@prisjakt/ui-app-context';
import Pagination from '@prisjakt/ui-pagination';
import Button from '@prisjakt/ui-button';
import { ScrollLink } from '@prisjakt/ui-scroll';
import ChevronDown from 'mdi-react/ChevronDownIcon';
import T from '@prisjakt/ui-translate';
import debounce from 'lodash.debounce';
import Spinner from '@prisjakt/ui-spinner';
import ListItem from './list-item';

import * as S from './styles';

import { Deleted, EditFunctions, Item, Mutation } from '../sharedTypes';
import { SidebarListsQuery, PageInfo } from '../../../../types';

interface ItemListProps {
  mutation?: Mutation;
  render?: (props: RenderProps) => void;
  refresh?: () => void;
  onFetchMore?: () => void;
  onClick: (id: number) => void;
  deleted?: Deleted;
  fetching?: boolean;
  pageInfo: PageInfo;
  renderEmpty: React.ReactNode;
  listId?: number;
  nodes: Lists['nodes'];
  children?: React.ReactNode;
}

type Lists = NonNullable<SidebarListsQuery['me']['lists']>;

interface State {
  optimisticQuantity: { [key in number]: number };
  queue: { [key in number]: boolean };
  transitionState: any;
}

type Props = ItemListProps & EditFunctions;

type RenderProps = ItemListProps & {
  transitionState: any;
};

class ItemList extends React.PureComponent<Props, State> {
  static defaultProps = {
    renderEmpty: '',
    pageInfo: {},
    fetching: false,
  };

  static contextType = AppContext;

  constructor(props: Props) {
    super(props);

    this.state = {
      optimisticQuantity: {},
      queue: {},
      transitionState: {},
    };

    this.setTransitionState = this.setTransitionState.bind(this);
  }

  flushQuantity = debounce(() => {
    this.commitQuantity();
  }, 1500);

  updateQuantity = (id: number, quantity: number) => {
    this.setState({
      optimisticQuantity: {
        ...this.state.optimisticQuantity,
        [id]: quantity,
      },
      queue: {
        ...this.state.queue,
        [id]: true,
      },
    });

    this.flushQuantity();
  };

  commitQuantity() {
    const { refresh, mutation } = this.props;
    const ids = Object.keys(this.state.queue);
    this.setState({ queue: {} });

    Promise.all(
      ids.map(
        id =>
          mutation &&
          mutation(
            `mutation listItemQuantity($listId: Int!, $id: ID!, $quantity: Int!) {
              listItemQuantity(listId: $listId, id: $id, quantity: $quantity) {
                success
              }
            }`,
            {
              quantity: this.state.optimisticQuantity[id],
              id,
              listId: this.props.listId,
            },
            {
              refresh: false,
            }
          )
      )
    );

    if (refresh) refresh();
  }

  setTransitionState(id: number, state: State) {
    this.setState(prevState => ({
      transitionState: {
        ...prevState.transitionState,
        [id]: state,
      },
    }));
  }

  renderFooter() {
    if (this.props.onFetchMore && !this.props.pageInfo.hasNext) {
      return null;
    }

    if (this.props.onFetchMore) {
      return (
        <S.LoadMore>
          {!this.props.fetching ? (
            <Button
              color="link"
              variant="text"
              icon={<ChevronDown />}
              iconPosition="bottom"
              onClick={this.props.onFetchMore}
            >
              <T domain="common">button.show-more</T>
            </Button>
          ) : (
            <Spinner position="none" isActive />
          )}
        </S.LoadMore>
      );
    }

    return (
      <ScrollLink
        shouldScroll={({ tagName }: { tagName: string }) => tagName === 'A'}
        to="item list"
      >
        <Pagination pageInfo={this.props.pageInfo} />
      </ScrollLink>
    );
  }

  render() {
    const { props } = this;

    if (props.nodes.length === 0) {
      return props.renderEmpty;
    }

    return (
      <>
        {props.nodes.map((originalItem: Item, index) => {
          const optimisticQuantity = this.state.optimisticQuantity[
            originalItem.id
          ];

          // Create a new item with optimistic updates
          const item = {
            ...originalItem,
            quantity:
              optimisticQuantity === undefined &&
              (originalItem.__typename === 'ListSearch' ||
                originalItem.__typename === 'ListProduct')
                ? originalItem.quantity
                : optimisticQuantity,
          };

          const listItemProps = {
            item,
            updateQuantity: this.updateQuantity,
            setTransitionState: this.setTransitionState,
            transitionState: this.state.transitionState,
            ...props,
          };

          return this.props.render ? (
            this.props.render(listItemProps)
          ) : (
            <ListItem key={index.toString()} {...listItemProps} />
          );
        })}

        {this.renderFooter()}
      </>
    );
  }
}

export default ItemList;
